
import { useState, useEffect, useRef } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Send, Paperclip, Loader2 } from "lucide-react";
import { CommissionMessage } from "@/types/commission.types";
import { ChatMessageList } from "@/components/ui/chat-message-list";
import { useUser } from "@/hooks/use-user";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  useCommissionMessages,
  useSendCommissionMessage,
  useMarkMessagesAsRead
} from "@/hooks/commission";

interface CommissionChatProps {
  requestId: string;
}

export function CommissionChat({ requestId }: CommissionChatProps) {
  const { user } = useUser();
  const [message, setMessage] = useState("");
  const { data: messages = [] } = useCommissionMessages(requestId);
  const { mutate: sendMessage, isPending: isSending } = useSendCommissionMessage();
  const { mutate: markAsRead } = useMarkMessagesAsRead();
  
  // Mark messages as read when the component mounts or messages change
  useEffect(() => {
    if (user && messages.length > 0) {
      markAsRead({ requestId, userId: user.id });
    }
  }, [user, messages, requestId, markAsRead]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !message.trim()) return;
    
    sendMessage({
      commission_request_id: requestId,
      sender_id: user.id,
      message: message.trim(),
      read: false,
    }, {
      onSuccess: () => {
        setMessage("");
      }
    });
  };

  return (
    <div className="flex flex-col h-[600px] border rounded-md overflow-hidden">
      <div className="p-3 bg-muted/30 border-b">
        <h3 className="font-medium">Mensagens da Comissão</h3>
      </div>
      
      <ChatMessageList className="px-2">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-8">
            <p className="text-muted-foreground">Nenhuma mensagem ainda. Inicie a conversa!</p>
          </div>
        ) : (
          messages.map((msg: CommissionMessage) => (
            <MessageBubble 
              key={msg.id} 
              message={msg} 
              isCurrentUser={user?.id === msg.sender_id} 
            />
          ))
        )}
      </ChatMessageList>
      
      <Separator />
      
      <form 
        onSubmit={handleSendMessage}
        className="p-3 bg-background flex items-end gap-2"
      >
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Digite sua mensagem..."
          className="resize-none min-h-[80px]"
          disabled={isSending}
        />
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            disabled={isSending}
            onClick={() => {
              // File attachment functionality would go here
              alert("Funcionalidade de anexo em breve!");
            }}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button type="submit" size="icon" disabled={isSending || !message.trim()}>
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface MessageBubbleProps {
  message: CommissionMessage;
  isCurrentUser: boolean;
}

function MessageBubble({ message, isCurrentUser }: MessageBubbleProps) {
  return (
    <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'} gap-2 max-w-[80%]`}>
        {!isCurrentUser && message.sender && (
          <Avatar className="h-8 w-8">
            <AvatarImage src={message.sender.avatar_url || ''} />
            <AvatarFallback>
              {message.sender.username?.charAt(0).toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        )}
        
        <div className="flex flex-col">
          <div
            className={`rounded-lg p-3 ${
              isCurrentUser
                ? 'bg-primary text-primary-foreground rounded-tr-none'
                : 'bg-muted rounded-tl-none'
            }`}
          >
            {message.message}
            
            {message.attachment_url && (
              <a 
                href={message.attachment_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className={`block mt-2 text-sm ${isCurrentUser ? 'text-primary-foreground/80' : 'text-blue-500'}`}
              >
                Ver anexo
              </a>
            )}
          </div>
          
          <span className={`text-xs text-muted-foreground mt-1 ${isCurrentUser ? 'text-right' : ''}`}>
            {formatDistanceToNow(new Date(message.created_at), { 
              addSuffix: true,
              locale: ptBR 
            })}
          </span>
        </div>
      </div>
    </div>
  );
}
